import _ from 'lodash'
import React from 'react'
import { evaluateExpressionWithScopes } from 'shared-libs/helpers/evaluation'
import { findAndClickFirstInput, getLabelProps, ILabelPropsSource } from './utils'
import { EntityPreview } from 'browser/components/atomic-elements/organisms/entity/entity-preview/entity-preview'
import { Hotkey, Hotkeys, HotkeysTarget } from '@blueprintjs/core'
import { findDOMNode } from 'react-dom'
import { getDebugId } from 'browser/app/utils/utils'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import { ComponentsContext } from 'browser/contexts/components/components-context'
import { FramesManager } from 'shared-libs/components/view/frames-manager'

// TODO: temporarily providing some base component props to quiet compiler
// errors, for now.
type TempProps = Partial<{
  entity: any
  label: any
  frames: any
  errors: any
  value: any
  focusHotkey: any

  showInlinePreview: any
  inlinePreviewSchema: any
  previewComponent: any
  uiSchemaPassthrough: any
}>

type OptionsProps = {
  enum?: any[]
  enumOptions?: any[]
  suggestions?: any[]
}

interface ISelectSchema extends OptionsProps {
  type?: string
  items?: ISelectSchema
  properties?: { value: { entityType?: string } }
}

interface ISelectPropsSource extends OptionsProps, ILabelPropsSource {
  entityType?: string
  formula?: string
  frames?: FramesManager
  optionLabelPath?: string
}

export function JSONSelectFactory<T extends TempProps>(
  SelectComponent: React.ComponentType<T>
): React.ComponentType<T & OptionsProps> {
  @HotkeysTarget
  class SelectWrapper extends React.Component<T & OptionsProps, any> {
    public static displayName = 'JSONSelectFactory'

    static contextType = ComponentsContext
    context!: React.ContextType<typeof ComponentsContext>

    constructor(props: T & OptionsProps) {
      super(props)
    }

    public render() {
      const { entity, frames, errors, value, showInlinePreview, inlinePreviewSchema, uiSchemaPassthrough, previewComponent } = this.props
      const dataDebugId = frames ? getDebugId(frames) : ''
      const errorText = errors && errors.join('\n')
      let preview = null

      if (value && !_.isEmpty(value) && (showInlinePreview || previewComponent)) {
        preview = <EntityPreview
          entity={entity}
          value={value}
          previewSchema={inlinePreviewSchema}
          previewComponent={previewComponent}
          uiSchema={uiSchemaPassthrough}
          components={this.context.components}
        />
      }

      const selectProps = this.getSelectProps(this.props)
      return (
        <div>
          <SelectComponent
            {...this.props}
            {...selectProps}
            data-debug-id={dataDebugId}
            errorText={errorText}
          />
          {preview}
        </div>
      )
    }

    public renderHotkeys() {
      const { focusHotkey, label } = this.props
      if (focusHotkey) {
        return (
          <Hotkeys>
            <Hotkey
              allowInInput={true}
              combo={focusHotkey}
              label={`Focus '${label}'`}
              onKeyDown={this.focus}
              stopPropagation={true}
              preventDefault={true}
              global={true}
            />
          </Hotkeys>
        )
      } else {
        return <Hotkeys />
      }
    }

    private focus = () => {
      // Will only work on wrapped components that use an input
      const element: any = findDOMNode(this)
      findAndClickFirstInput(element)
    }

    private getEntityType(schema: ISelectSchema, props: ISelectPropsSource) {
      const { entityType, isLabelEditable } = props
      // if label is editable, extract entityType from labeled schema
      if (isLabelEditable) {
        const valueSchema = schema.properties!.value
        return valueSchema.entityType
      }
      // use entityType from props
      return entityType
    }

    private getOptions(
      schema: ISelectSchema | undefined,
      props: ISelectPropsSource,
      optionLabelPath: string | null = null
    ) {
      if (schema?.type === 'array') {
        // if property is an array, then the select is a multi select. This means that the
        // options are on the item. E.g.
        // ratingMethods: { items: { enum: [...], type: string }, type: array }
        schema = schema.items
      }

      const enums = schema?.enum ?? props?.enum ?? []
      const enumOptions = schema?.enumOptions ?? props?.enumOptions ?? []
      const suggestions = schema?.suggestions ?? props?.suggestions ?? []
      const { formula } = props

      let options: any
      if (formula) {
        /*
        Existing enums and suggestions can be manipulated by referencing `data` in the formula prop,
        where `data` is the list of enums or suggestions.
        e.g. If you want to filter a license plate state enum down to a subset of states defined
        by your workflow, you can do something like this:
        `FILTER(data, (state) => (myExecution.validStates).includes(state))`
        */
        const scopes = {
          ...CustomFormulas,
          data: !_.isEmpty(enums) ? enums : suggestions,
        }
        options = evaluateExpressionWithScopes(props.frames!, formula, scopes)
      } else {
        if (!_.isEmpty(enumOptions)) {
          return enumOptions
        }
        options = !_.isEmpty(enums) ? enums : suggestions
      }

      return _.map(options, (option) => ({
        value: option,
        label: this.extractLabel(schema, option, optionLabelPath),
      }))
    }

    private extractLabel(
      schema: ISelectSchema | undefined,
      option: unknown,
      optionLabelPath: string | null = null
    ) {
      if (schema?.type === 'object') {
        return _.get(option, String(optionLabelPath), '')
      }

      if (_.isObject(option)) {
        // attempt to extract label anyway instead of allowing label to be an object, which crashes
        // react.
        return _.get(option, String(optionLabelPath), option)
      }

      return option
    }

    private getSelectProps(props: ISelectPropsSource) {
      const { frames, optionLabelPath } = props
      const schema = frames?.getContext('dataSchema')
      return {
        entityType: this.getEntityType(schema, props),
        labelProps: getLabelProps(schema, props),
        options: this.getOptions(schema, props, optionLabelPath),
      }
    }
  }

  return SelectWrapper
}
